import { packageDayRangeCount, packageTripDayCount, readPackageDayOffsets } from './packageDayFields.js';
import { countItineraryTripDays } from './itineraryDayNumber.js';

const toDateKey = (value) => String(value || '').split('T')[0];

function parseDateParts(value) {
  const key = toDateKey(value);
  const parts = key.split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((n) => Number(n));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { year: y, month: m, day: d };
}

/** Local calendar date key (YYYY-MM-DD) + whole days — avoids ISO timezone drift. */
export function addDaysToDateKey(dateKeyOrIso, dayDelta) {
  const parts = parseDateParts(dateKeyOrIso);
  if (!parts || !Number.isFinite(dayDelta)) return null;
  const d = new Date(parts.year, parts.month - 1, parts.day);
  d.setDate(d.getDate() + dayDelta);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

/** Package start_day on the shared itinerary timeline (Event Detail uses this for date shift). */
export function readPackageStartDay(pkg) {
  if (!pkg || typeof pkg !== 'object') return 0;
  const raw = pkg.startDayOffset ?? pkg.startDay ?? pkg.start_day;
  if (raw == null || raw === '') return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function batchDurationDays(batchStartDate, batchEndDate) {
  if (!batchStartDate || !batchEndDate) return null;
  const parts1 = parseDateParts(batchStartDate);
  const parts2 = parseDateParts(batchEndDate);
  if (!parts1 || !parts2) return null;
  const s = new Date(parts1.year, parts1.month - 1, parts1.day);
  const e = new Date(parts2.year, parts2.month - 1, parts2.day);
  const diff = Math.round((e - s) / (24 * 60 * 60 * 1000)) + 1;
  return diff >= 1 ? diff : null;
}

export function normalizeEnabledScheduleIds(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return trimmed
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
    }
  }
  return [];
}

/** Ordered schedule rows linked via enabled_schedule_ids. */
function enabledScheduleDaysForPackage(pkg, allScheduleDays) {
  const enabledIds = normalizeEnabledScheduleIds(pkg?.enabledScheduleIds ?? pkg?.enabled_schedule_ids);
  if (!enabledIds.length || !Array.isArray(allScheduleDays) || !allScheduleDays.length) {
    return [];
  }
  const byDayId = new Map(allScheduleDays.map((day) => [String(day.id), day]));
  const ordered = enabledIds.map((id) => byDayId.get(String(id))).filter(Boolean);
  return ordered;
}

/** @deprecated Prefer countItineraryTripDays on enabled rows. */
export function countEnabledScheduleDaysForPackage(pkg, allScheduleDays) {
  return enabledScheduleDaysForPackage(pkg, allScheduleDays).length;
}

function extractDurationDaysFromText(text) {
  const s = String(text || '');
  if (!s) return null;
  const m = s.match(/\b(\d{1,2})\s*day(s)?\b/i);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n <= 0 || n > 60) return null;
  return n;
}

function mainPackageDurationDays(mainPkg, trekDurationDays) {
  if (mainPkg?.durationDays != null && Number(mainPkg.durationDays) > 0) {
    return Number(mainPkg.durationDays);
  }
  const enabledIds = normalizeEnabledScheduleIds(mainPkg?.enabledScheduleIds ?? mainPkg?.enabled_schedule_ids);
  if (enabledIds.length > 0) {
    return enabledIds.length;
  }
  if (Number(trekDurationDays) > 0) return Number(trekDurationDays);
  const span = packageTripDayCount(mainPkg);
  if (span != null && span > 0) return span;
  return null;
}

/**
 * Join-from card duration:
 * 1) enabled schedule row count (matched with itinerary days)
 * 2) normalized enabled schedule IDs count
 * 3) explicit durationDays
 * 4) main package duration adjusted by join start & end day offsets
 * 5) text / trek fallback
 */
export function resolveJoinPackageDays(pkg, options = {}) {
  const { mainPackage = null, trekDurationDays = null, allScheduleDays = [] } = options;
  const trekDays = Number(trekDurationDays) > 0 ? Number(trekDurationDays) : null;

  const enabledDays = enabledScheduleDaysForPackage(pkg, allScheduleDays);
  if (enabledDays.length > 0) {
    const tripDays = countItineraryTripDays(enabledDays);
    if (tripDays > 0) return tripDays;
  }

  const enabledIds = normalizeEnabledScheduleIds(pkg?.enabledScheduleIds ?? pkg?.enabled_schedule_ids);
  if (enabledIds.length > 0) {
    return enabledIds.length;
  }

  if (pkg?.durationDays != null && Number(pkg.durationDays) > 0) {
    return Number(pkg.durationDays);
  }

  const main = mainPackage ? readPackageDayOffsets(mainPackage) : { start: null, end: null };
  const join = readPackageDayOffsets(pkg);
  const mainDur = mainPackageDurationDays(mainPackage, trekDays);

  if (
    mainPackage &&
    main.start != null &&
    main.end != null &&
    join.start != null &&
    join.end != null &&
    mainDur != null
  ) {
    const startShift = (join.start ?? 0) - (main.start ?? 0);
    const endShift = (join.end ?? 0) - (main.end ?? 0);
    const computedDays = mainDur - startShift - endShift;
    if (computedDays > 0) {
      return computedDays;
    }
  }

  const joinOnlySpan = packageTripDayCount(pkg);
  if (joinOnlySpan != null && joinOnlySpan > 0) return joinOnlySpan;

  const fromText =
    extractDurationDaysFromText(pkg?.name) ??
    extractDurationDaysFromText(pkg?.description) ??
    extractDurationDaysFromText(pkg?.reportingMessage);
  if (fromText) return fromText;

  return trekDays ?? mainDur ?? 1;
}

/**
 * Order-summary duration + calendar range for a selected join package.
 * Batch start = reference (main) package departure; other packages shift by start_day delta.
 */
export function resolveJoinPackageOrderSummary(selectedPkg, options = {}) {
  const {
    mainPackage = null,
    batchStartDate = null,
    batchEndDate = null,
    allScheduleDays = []
  } = options;

  const referencePackage = mainPackage || selectedPkg;
  const trekDurationFromBatch = batchDurationDays(batchStartDate, batchEndDate);

  const durationDays = selectedPkg
    ? resolveJoinPackageDays(selectedPkg, {
        mainPackage: referencePackage,
        trekDurationDays: trekDurationFromBatch,
        allScheduleDays
      })
    : trekDurationFromBatch ?? 7;

  let startDate = null;
  let endDate = null;

  if (batchStartDate && selectedPkg) {
    const refStartDay = readPackageStartDay(referencePackage);
    const activeStartDay = readPackageStartDay(selectedPkg);
    const startShift = activeStartDay - refStartDay;
    startDate = addDaysToDateKey(batchStartDate, startShift);
    endDate = startDate ? addDaysToDateKey(startDate, durationDays - 1) : null;
  } else if (batchStartDate && batchEndDate) {
    startDate = toDateKey(batchStartDate);
    endDate = toDateKey(batchEndDate);
  }

  return {
    durationDays,
    durationNights: Math.max(0, durationDays - 1),
    startDate,
    endDate
  };
}
